import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { useDispatch, useSelector } from 'react-redux';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import { converters, HSLColor } from '@yola/yola-palette-generator';
import dialogTypes from '../../dialogs/constants/dialog-types';
import segment from '../../analytics/segment';
import helpers from '../helpers';
import actions from '../actions';
import triggerIds from '../constants/trigger-ids';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import BaseColorsPickerDialog from '../components/base-colors-picker-dialog';
import { ACCENT_COLOR_TYPE, DARK_COLOR_TYPE } from '../constants/color-types';
import { FALLBACK_COLOR_SCHEME } from '../constants/color-schemes';

const { hsl2hex } = converters;

const {
  colorValidation,
  getAdjustedBaseColors,
  mapColorKeyToColorType,
  parseHslColorString,
  updatePalettesWith,
  createPaletteEngine,
  updateLivePaletteElement,
} = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const getCaptions = (cameFromAddButton, colorType) => {
  const lightWarning = i18next.t(
    'We recommend using a lighter shade to increase the legibility on your website.'
  );
  const darkWarning = i18next.t(
    'We recommend using a darker shade to increase the legibility on your website.'
  );

  const warning = colorType === DARK_COLOR_TYPE ? darkWarning : lightWarning;

  return {
    submitButton: i18next.t('Save'),
    cancelButton: i18next.t('Cancel'),
    breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
    title: cameFromAddButton ? i18next.t('New palette') : i18next.t('Edit palette'),
    description: i18next.t(
      'Select favorite colors for the palette to generate color schemes for the blocks on your website'
    ),
    tooltips: {
      'ws-primary-0': i18next.t(
        'This color is used for primary button, active navigation items, and graphic elements'
      ),
      'ws-secondary-0': i18next.t(
        'This color is used for links, feature and social icons, and other design elements'
      ),
    },
    presetsSubtitle: i18next.t('Presets'),
    warning,
  };
};

const BaseColorsPickerContainer = ({
  getSharedData,
  onDialogMainAction,
  onDialogCancel,
  resolveSharedData,
  triggerId,
}) => {
  const {
    offsetX,
    offsetY,
    originPalette,
    engine: sharedEngine,
    carriedCustomPalettes,
  } = getSharedData();

  const dispatch = useDispatch();
  const defaultColorPalettes = useSelector(customization.selectors.getColorPalettes);
  const colorPaletteConfig = useSelector(customization.selectors.getColorPaletteConfig);

  const cameFromAddButton = triggerId === triggerIds.ADD_CUSTOM_PALETTE;

  const [engine] = useState(
    () =>
      sharedEngine ||
      createPaletteEngine(originPalette, {
        forceClone: cameFromAddButton,
      })
  );

  const [initialPalette] = useState(() => engine.exportState());

  const [colorItems, setColorItems] = useState(initialPalette.colors);
  const [dialogPosition, updatePosition] = useState({ offsetX, offsetY });
  const [selectedColorKey, setSelectedColorKey] = useState(FALLBACK_COLOR_SCHEME);
  const [isWarningShown, setWarningVisibility] = useState(false);
  const workerRef = useRef(null);
  const colorType = mapColorKeyToColorType(selectedColorKey);
  const captions = getCaptions(cameFromAddButton, colorType);

  const applyCompiledPalette = useCallback(
    (compiledPalette) => {
      updateLivePaletteElement(compiledPalette);
      dispatch(actions.changeColor(compiledPalette.variables));
    },
    [dispatch]
  );

  const updateSharedData = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleSubmit = () => {
    const compiledPalette = engine.updateColors(colorItems);
    applyCompiledPalette(compiledPalette);

    const submittedPalette = engine.exportState();
    onDialogMainAction({
      triggerId,
      adjustedColors: getAdjustedBaseColors(originPalette, submittedPalette),
      colorPreset: originPalette.id,
    });

    if (cameFromAddButton) {
      updateSharedData({
        carriedActivePaletteId: compiledPalette.id,
        carriedCustomPalettes: updatePalettesWith(submittedPalette, carriedCustomPalettes),
      });

      dispatch(
        dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
          sourceId: dialogCallSourceIds.BACK_FROM_COLOR_PALETTE_SETTINGS,
        })
      );
      return;
    }

    updateSharedData({ engine });

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId,
        colorPreset: initialPalette.id,
      })
    );
  };

  const handleCancel = () => {
    workerRef.current?.terminate();

    const isBaseColorsChanged = colorItems !== initialPalette.colors;

    if (isBaseColorsChanged) {
      const compiledPalette = engine.updateColors(initialPalette.colors);
      applyCompiledPalette(compiledPalette);
    }

    onDialogCancel({
      triggerId,
    });

    if (cameFromAddButton) {
      dispatch(
        dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
          sourceId: dialogCallSourceIds.BACK_FROM_COLOR_PALETTE_SETTINGS,
        })
      );
      return;
    }

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId,
        colorPreset: initialPalette.id,
      })
    );
  };

  const handleColorChange = ({ hsl }) => {
    setColorItems((prev) => ({
      ...prev,
      [selectedColorKey]: new HSLColor(...Object.values(hsl)).toCSSValue(false),
    }));
  };

  const handlePresetSelect = (preset) => {
    setColorItems(preset.colors);

    trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_CHANGED, {
      dialogId: dialogTypes.BASE_COLORS_PICKER_DIALOG,
      colorPreset: preset.id,
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const handleActiveColorSelect = (colorKey) => {
    setSelectedColorKey(colorKey);

    const activeColorType = mapColorKeyToColorType(colorKey);

    if (activeColorType === ACCENT_COLOR_TYPE) {
      setWarningVisibility(false);
      return;
    }

    const { h, s, l } = parseHslColorString(colorItems[colorKey]);
    const hslColor = new HSLColor(h, s, l);

    setWarningVisibility(!colorValidation(hsl2hex(hslColor), activeColorType));
  };

  const handleColorChangeComplete = ({ hex }) => {
    if (colorType !== ACCENT_COLOR_TYPE) {
      setWarningVisibility(!colorValidation(hex, colorType));
    }
  };

  // The worker owns a separate engine instance for live preview during drag.
  // Submit and cancel use the main-thread engine directly because they need
  // synchronous results and the worker preview is fire-and-forget.
  useEffect(() => {
    const workerListener = ({ data }) => {
      applyCompiledPalette(data.palette);
    };

    workerRef.current = new Worker(
      new URL('../workers/update-palette-colors.worker.js', import.meta.url)
    );
    workerRef.current.addEventListener('message', workerListener);

    workerRef.current.postMessage({
      type: 'init',
      palette: initialPalette,
      config: colorPaletteConfig,
    });

    return () => {
      workerRef.current.removeEventListener('message', workerListener);
      workerRef.current.terminate();
    };
  }, [applyCompiledPalette, initialPalette, colorPaletteConfig]);

  useEffect(() => {
    if (!bowser.mobile && workerRef.current) {
      workerRef.current.postMessage({ colorItems });
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [colorItems]);

  return (
    <BaseColorsPickerDialog
      colors={colorItems}
      selectedColor={selectedColorKey}
      presets={defaultColorPalettes}
      captions={captions}
      displayWarning={isWarningShown}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      onActiveColorSelect={handleActiveColorSelect}
      onColorChange={handleColorChange}
      onColorChangeComplete={handleColorChangeComplete}
      onPresetSelect={handlePresetSelect}
    />
  );
};

BaseColorsPickerContainer.propTypes = {
  getSharedData: PropTypes.func.isRequired,
  triggerId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default BaseColorsPickerContainer;
